import React from 'react';
import ReactEcharts from 'echarts-for-react';
import 'echarts-wordcloud';
import { useIntl } from 'umi';
import { formatter } from '@/pages/opinion/components/ChartCommon/formatter';
import { mathFormat, sentimentColorList } from '@/pages/opinion/utils';
import { sentimentColorMap } from '@/pages/opinion/constants';
import { useDrilldownParams } from '@op/hooks/useDrilldownParams';
import { buriedBtnPoint } from '@/pages/opinion/operationLog/buriedPoint';
import { brandHealthFormat, sentimentFormat } from '@op/utils/value';
import useKeywordWordCloud from '@op/hooks/useKeywordWordCloud';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import '../index.less';

const Wordcloud = (props: {
  height: any;
  wordData: any;
  keywordData: any;
  language: string;
  filter: any;
  chartClickAble?: boolean;
  needBrandHealth?: boolean;
  statsDetailType?: string;
  keywordClick?: (params: any) => void;
}) => {
  const {
    height,
    wordData,
    keywordData,
    language,
    filter,
    chartClickAble = true,
    needBrandHealth = false,
    statsDetailType = 'performance-keyword',
    keywordClick,
  } = props;
  const { goPage } = useDrilldownParams();

  const { formatMessage } = useIntl();

  // console.log('wordData', wordData);
  const { Keyword_Color } = useGetSingePageSourceRule({
    keys: 'Keyword_Color',
  });
  const wordOption = () => {
    const option = {
      // backgroundColor: '#fff',
      tooltip: {
        formatter: (params) => {
          const { name, originName, enName, mentions, sentiment, showMore } =
            params.data || {};

          const title = showMore ? '' : name;
          const addMore = showMore
            ? [
              {
                type: formatMessage({ id: 'English' }),
                score: enName,
              },
              {
                type: formatMessage({ id: 'Original Language' }),
                score: originName,
              },
            ]
            : [];

          const table = {
            records: [
              ...addMore,
              {
                type: formatMessage({ id: 'Mentions' }),
                score: mathFormat(mentions),
              },
              ...(Keyword_Color
                ? []
                : [
                  {
                    type: formatMessage({
                      id: needBrandHealth ? 'Brand Health' : 'Sentiment',
                    }),
                    score: needBrandHealth
                      ? brandHealthFormat(sentiment)
                      : sentimentFormat(sentiment),
                  },
                ]),
            ],
            showHeader: false,
            columns: [
              {
                key: 'type',
              },
              {
                key: 'score',
                weight: 'bold',
                align: 'right',
              },
            ],
          };

          return formatter({
            title,
            tables: [table],
          });
        },
        borderWidth: 0,
        confine: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        padding: 0,
        textStyle: {
          fontSize: 12,
        },
      },
      series: [
        {
          type: 'wordCloud',
          shape: function shapeSquare(theta) {
            return Math.min(
              1 / Math.abs(Math.cos(theta)),
              1 / Math.abs(Math.sin(theta)),
            );
          },
          gridSize: wordData?.length >= 25 ? 20 : 24,
          sizeRange: [12, 48],
          rotationRange: [0, 0, 0, 0],
          layoutAnimation: true,
          textStyle: {
            color (param: any) {
              const colorIndex = Keyword_Color ? 2 : param.data.colorIndex;
              return sentimentColorList[colorIndex];
            },
          },
          emphasis: {
            textStyle: {
              fontWeight: 600,
            },
          },
          left: 0,
          top: 0,
          right: null,
          bottom: null,
          width: '100%',
          height: '100%',
          drawOutOfBound: false,
          shrinkToFit: true,
          data: wordData || [],
        },
      ],
    };
    return option;
  };

  const onChartClick = (params) => {
    if (!chartClickAble) return;

    const transObj: any = {
      origin: 'keyword',
      en: 'keyword_en',
      zh: 'keyword_cn',
    };

    const keywordItem = keywordData?.filter(
      (v: any) => v[transObj[language]] == params?.name,
    )[0];

    const keywordChild = keywordItem?.child;

    let keyword = [];

    if (Array.isArray(keywordChild) && keywordChild?.length > 0) {
      keyword = keywordChild.map((item) => item.keyword);
    }
    if (keywordClick) {
      return keywordClick({ params: keyword, title: params?.name });
    }
    buriedBtnPoint('TK_keyword', keywordItem?.keyword);
    goPage({
      path: '/opinion/Stats/Detail',
      filter: {
        filter: {
          ...filter,
          keyword,
          need_brand_health: needBrandHealth,
        },
        statsDetailType,
        needBrandHealth,
      },
    });
  };

  const customLegends = [
    {
      text: formatMessage({ id: 'Positive' }),
      color: sentimentColorMap[0].color,
    },
    {
      text: formatMessage({ id: 'Neutral' }),
      color: sentimentColorMap[1].color,
    },
    {
      text: formatMessage({ id: 'Negative' }),
      color: sentimentColorMap[2].color,
    },
  ];

  const onEvents = {
    click: onChartClick,
  };

  useKeywordWordCloud({
    data: wordData,
    parentClassName: '.word-cloud-wrapper',
  });

  return (
    <div className="bubbleChatsBox dbo-videobox-cover">
      <div
        className="WordcloudCharts"
        style={{ height: `${height  }px`, width: '100%' }}
      >
        <ReactEcharts
          style={{ height: `${height  }px`, width: '100%' }}
          option={wordOption()}
          // theme="ThemeStyle"
          lazyUpdate={true}
          className="word-cloud-wrapper"
          opts={{ renderer: 'svg' }}
          notMerge={true}
          onEvents={onEvents}
        />
      </div>
      {!Keyword_Color && (
        <div className="custom-legend-box">
          {customLegends.map(({ text, color }, index) => (
            <p key={index}>
              <span
                className="custom-legend-box-icon"
                style={{ backgroundColor: color }}
              />
              <span className="custom-legend-box-text">{text}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
};
export default React.memo(Wordcloud);
